/**
 * The words on the application's page that are not a question, as they ship.
 *
 * The same as APPLICATION_PAGE_COPY_DEFAULTS in functions/src/employment/applicationSchema.js,
 * which is what the server falls back to; applicationPageCopy.test.js holds the two together. The
 * page uses these before the server answers, and the live editor shows them as the defaults a
 * cleared box goes back to.
 */
export const DEFAULT_PAGE_COPY = {
  intro:
    'Please answer every question. The longest parts ask for your employment history over the past ten years and three professional references, so it is worth having your records to hand.' +
    String.fromCharCode(10, 10) +
    '**Your answers save automatically.** You can close this page and come back to the same link.',
  voluntaryNote:
    'Answering this part is entirely your choice, and choosing not to will not affect your application in any way. If you do answer, it is filed separately from your application and is not used to decide whether to employ you.',
  submitLabel:
    'Submit my application',
  title:
    'Application for employment',
  thankYou:
    'Thank you — your application has been submitted.',
  thankYouDetail:
    'Keep a copy for your records if you would like one.',
  downloadLabel:
    'Download a PDF copy',
  saveLabel:
    'Save and finish later',
  savingLabel:
    'Saving…',
  sendingLabel:
    'Sending…',
  previewLabel:
    'Preview as PDF',
  savedLabel:
    'Saved {time}',
  attentionOne:
    'One answer still needs your attention:',
  attentionMany:
    '{count} answers still need your attention:',
  requiredMessage:
    '{question} is required.',
  tableRequiredMessage:
    '{section}: you answered Yes, so at least one entry is needed.',
  usuallyProvidedMessage:
    '{question} is usually provided. Add an entry, or say briefly why there is none.',
  mustAgreeMessage:
    '{section}: this has to be agreed to before the form can be submitted.',
  columnRequiredMessage:
    '{question}, entry {row}: {column} is required.',
  clearChoice:
    'Clear my answer',
  reasonPrompt:
    'If there is nothing to list here, tell us briefly why:',
  notGiven:
    'Not given',
  noticeLoading:
    'Loading the privacy notice…',
  submitFailed:
    'Something went wrong sending your application. Your answers are saved; please try again in a moment.',
  alreadySubmitted:
    'This application has already been submitted. Thank you — there is nothing more to do.',
  linkIncomplete:
    'This link is incomplete. Open the link exactly as it was sent to you.',
  linkInvalid:
    'This link is not valid. Open it from the email we sent you.',
  linkExpired:
    'This link has expired. Reply to the email we sent you and we will send a new one.',
  linkMismatch:
    'This link does not match this application. Open the link from your email.',
  linkReplaced:
    'This link has been replaced by a newer one. Please open the most recent email we sent you.',
  linkSwitchedOff:
    'This link has been switched off. If you were expecting to complete an application, reply to the email we sent you and we will look into it.',
  linkNotFound:
    'We could not find this application.',
  linkFailed:
    'This link could not be opened.',
  tabTitle:
    'Application for Employment — AALRR',
};

/**
 * Wording that, until somebody sets it, is another key's: the privacy notice's section note used to
 * be the voluntary note, shown on both separately-filed sections. As the server has it.
 */
export const PAGE_COPY_FALLS_BACK_TO = { privacyNote: 'voluntaryNote' };

/** Wording that may be taken off the page (`pageCopy.hidden`), as the server has it. */
export const HIDEABLE_PAGE_COPY = ['intro', 'voluntaryNote', 'privacyNote', 'thankYouDetail', 'downloadLabel', 'previewLabel', 'savedLabel'];

/** Which page message a dead link shows, by the reason the server gave. As the server has it. */
export const LINK_MESSAGE_KEYS = {
  missing_token: 'linkIncomplete',
  malformed_token: 'linkInvalid',
  bad_signature: 'linkInvalid',
  malformed_payload: 'linkInvalid',
  expired: 'linkExpired',
  wrong_link: 'linkMismatch',
  link_superseded: 'linkReplaced',
  link_revoked: 'linkSwitchedOff',
  not_found: 'linkNotFound',
};

/** `{name}`-style blanks filled from `values`; an unknown one is left as typed, so a typo shows. */
export const fillWords = (text, values) =>
  String(text || '').replace(/\{(\w+)\}/g, (whole, key) =>
    values && Object.prototype.hasOwnProperty.call(values, key) ? String(values[key] ?? '') : whole
  );

/**
 * A draft's page wording with the defaults filled in, the way the server resolves it. Hidden
 * wording comes out empty, and the page leaves its box out.
 */
export function resolvePageCopy(stored) {
  const source = stored && typeof stored === 'object' ? stored : {};
  const own = (key) => (typeof source[key] === 'string' ? source[key].trim() : '');
  const hidden = Array.isArray(source.hidden) ? source.hidden : [];
  const result = {};
  for (const [key, fallback] of Object.entries(DEFAULT_PAGE_COPY)) {
    result[key] = own(key) || fallback;
  }
  for (const [key, from] of Object.entries(PAGE_COPY_FALLS_BACK_TO)) {
    result[key] = own(key) || result[from];
  }
  for (const key of HIDEABLE_PAGE_COPY) if (hidden.includes(key)) result[key] = '';
  // Kept on the result, so resolving it again (the applicant's page does, with what the server
  // sent) leaves hidden wording empty rather than filling it back in with the defaults.
  result.hidden = HIDEABLE_PAGE_COPY.filter((key) => hidden.includes(key));
  return result;
}
